import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuizStore } from "../state/store";
import type { ProgressRecord, VocabEntry } from "../lib/types";

type Filter = "all" | "marked" | "wrong";

const FILTER_LABELS: Record<Filter, string> = {
  all: "Alle",
  marked: "★ Markierte",
  wrong: "Falsch beantwortete",
};

/**
 * /words — Vokabel-Listenübersicht (Issue #123, Spec 50 §7).
 *
 * Zeigt alle aktiven Vokabeln, mit Filter-Pills "Alle | Markierte | Falsch
 * beantwortete" und pro Zeile einem Stern-Toggle zum Markieren. Der Filter
 * landet als `?filter=…` in der URL, damit Stats-Tiles direkt einen
 * vorgefilterten Link aufrufen können.
 */
export function WordsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get("filter");
  const filter: Filter =
    filterParam === "marked" || filterParam === "wrong" ? filterParam : "all";

  const vocab = useQuizStore((s) => s.vocab);
  const progress = useQuizStore((s) => s.progress);
  const markers = useQuizStore((s) => s.markers);
  const toggleMarked = useQuizStore((s) => s.toggleMarked);
  const status = useQuizStore((s) => s.status);
  const loadAll = useQuizStore((s) => s.loadAll);

  // Direkter Browser-Navigation auf /words umgeht App.tsx, das sonst loadAll
  // triggert. Ohne diesen useEffect bleibt `vocab` leer und die Liste zeigt
  // nichts — bei deep-link aus dem StatsScreen-Tile oder Browser-Reload sah
  // der User eine leere Seite (#123 Phase 6 E2E-Fail).
  useEffect(() => {
    if (status === "idle" || vocab.length === 0) {
      void loadAll();
    }
  }, [status, vocab.length, loadAll]);

  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = vocab.filter((entry) => {
      if (filter === "marked" && !markers.has(entry.english)) return false;
      if (filter === "wrong") {
        // "Falsch beantwortet" = mindestens eine Direction-Row hat state='wrong'.
        const enDeKey = `${entry.english}|en_de`;
        const deEnKey = `${entry.english}|de_en`;
        const isWrong =
          progress.get(enDeKey)?.state === "wrong" ||
          progress.get(deEnKey)?.state === "wrong";
        if (!isWrong) return false;
      }
      if (q) {
        return (
          entry.english.toLowerCase().includes(q) ||
          entry.german.toLowerCase().includes(q)
        );
      }
      return true;
    });
    return base.sort((a, b) => a.english.localeCompare(b.english));
  }, [vocab, progress, markers, filter, query]);

  function setFilter(next: Filter) {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("filter");
    else params.set("filter", next);
    setSearchParams(params, { replace: true });
  }

  return (
    <main className="min-h-full">
      <div className="mx-auto w-full max-w-2xl px-4 pb-24 sm:px-6 sm:pb-8 space-y-4 safe-top">
        <header className="flex items-center justify-between gap-3">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-fg">
            Meine Vokabeln
          </h1>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="min-h-[44px] px-3 py-2 rounded-lg text-sm text-fg-muted hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
            data-testid="words-back"
          >
            ← Zurück
          </button>
        </header>

        <input
          type="search"
          inputMode="search"
          placeholder="Suchen (Englisch oder Deutsch)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="words-search"
          className="w-full min-h-[44px] px-3 py-2 rounded-lg bg-card border border-outline text-fg placeholder:text-fg-faintest focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />

        <div
          role="tablist"
          aria-label="Vokabel-Filter"
          className="grid grid-cols-3 gap-1.5 bg-bg/40 border border-outline-soft rounded-lg p-1"
          data-testid="words-filter"
        >
          {(["all", "marked", "wrong"] as const).map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                role="tab"
                type="button"
                aria-selected={active}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`min-h-[44px] rounded-md text-xs sm:text-sm font-semibold transition-colors px-2 ${
                  active
                    ? "bg-accent text-accent-on shadow-card"
                    : "text-fg-muted hover:text-fg hover:bg-card"
                }`}
              >
                {FILTER_LABELS[f]}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-fg-faint" data-testid="words-count">
          {rows.length} {rows.length === 1 ? "Vokabel" : "Vokabeln"}
        </p>

        {rows.length === 0 ? (
          <WordsEmpty filter={filter} />
        ) : (
          <ul className="space-y-1.5" data-testid="words-list">
            {rows.map((entry) => (
              <WordRow
                key={entry.english}
                entry={entry}
                marked={markers.has(entry.english)}
                progress={progress}
                onToggle={() => void toggleMarked(entry.english)}
              />
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}

function WordsEmpty({ filter }: { filter: Filter }) {
  let title: string;
  let body: React.ReactNode;
  if (filter === "marked") {
    title = "Noch keine Vokabeln markiert";
    body = (
      <>
        Tippe im Quiz auf den Stern oben rechts oder in dieser Liste auf das ☆
        neben einer Vokabel, um sie zu markieren. Anschließend kannst du im
        Settings-Menü unter <span className="font-semibold text-fg">Modus → ★ Markierte</span>
        ausschließlich diese Vokabeln üben.
      </>
    );
  } else if (filter === "wrong") {
    title = "Keine falsch beantworteten Vokabeln";
    body = (
      <>
        Aktuell hat keine Vokabel den Status „falsch". Wenn du eine Vokabel im
        Quiz falsch beantwortest, taucht sie hier auf, bis du sie wieder
        richtig beantwortest.
      </>
    );
  } else {
    title = "Keine Treffer";
    body = "Versuche einen anderen Suchbegriff.";
  }
  return (
    <div
      className="bg-card rounded-xl border border-outline-soft px-4 py-6 text-center space-y-2"
      data-testid="words-empty"
    >
      <p className="text-base font-semibold text-fg">{title}</p>
      <p className="text-sm text-fg-soft leading-relaxed max-w-md mx-auto">{body}</p>
      <Link
        to="/"
        className="inline-block mt-2 text-sm text-accent hover:underline"
        data-testid="words-empty-back"
      >
        ← Zurück zum Quiz
      </Link>
    </div>
  );
}

function WordRow({
  entry,
  marked,
  progress,
  onToggle,
}: {
  entry: VocabEntry;
  marked: boolean;
  progress: Map<string, ProgressRecord>;
  onToggle: () => void;
}) {
  const enDe = progress.get(`${entry.english}|en_de`);
  const deEn = progress.get(`${entry.english}|de_en`);
  const wrong = enDe?.state === "wrong" || deEn?.state === "wrong";
  const mastered =
    enDe?.state === "mastered" && deEn?.state === "mastered";
  return (
    <li
      className="flex items-center gap-2 bg-card rounded-lg border border-outline-soft px-3 py-2"
      data-testid="word-row"
      data-english={entry.english}
      data-marked={marked}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-fg truncate" lang="en">
            {entry.english}
          </span>
          {wrong && (
            <span className="shrink-0 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-danger-soft text-danger border border-danger/40 uppercase tracking-wide">
              Falsch
            </span>
          )}
          {mastered && (
            <span className="shrink-0 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-success-soft text-success border border-success/40 uppercase tracking-wide">
              Mast.
            </span>
          )}
        </div>
        <div className="text-sm text-fg-soft truncate" lang="de">
          {entry.german}
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={marked}
        aria-label={marked ? "Markierung entfernen" : "Vokabel markieren"}
        data-testid="row-star"
        data-marked={marked}
        className={`shrink-0 min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-full border text-xl transition-colors ${
          marked
            ? "bg-warning-soft text-warning border-warning/40"
            : "bg-bg/40 text-fg-faint border-outline-soft hover:text-warning hover:border-warning/30"
        } focus:outline-none focus-visible:ring-2 focus-visible:ring-warning`}
      >
        <span aria-hidden>{marked ? "★" : "☆"}</span>
      </button>
    </li>
  );
}
